import { useEffect, useState } from 'react'

import {
  Clock01Icon,
  Link04Icon,
  Location01Icon,
  UserCircleIcon
} from '@hugeicons/react'
import { DateTime } from 'luxon'
import { Drawer } from 'vaul'

import { useResourceRecordsContext } from '@/shared/hooks'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { useBodyScrollLock } from '@/shared/hooks/useBodyScrollLock'
import { sanitizeHtml } from '@/shared/lib/html/sanitize'
import { Badge } from '@/shared/ui/Badge'
import { DismissButton } from '@/shared/ui/DismissButton'
import { HeartReactionButton } from '@/shared/ui/Reactions/HeartReactionButton'

import { ScheduleKeywordCheckInControls } from './ScheduleKeywordCheckInControls'

import { getProgrammingKind } from '../model/kind'
import { canCheckInForSession, getTemporalStatus } from '../model/temporal'
import type { Session } from '../model/types'

import { RewardPill } from '@/shared/ui/RewardPill'

type ScheduleDetailSheetProps = {
  session: Session | null
  onClose: () => void
  /** Chamado quando o check-in por palavra conclui — a tela decide o que fazer. */
  onCheckInFinished?: () => void
}

export const ScheduleDetailSheet = ({
  session,
  onClose,
  onCheckInFinished
}: ScheduleDetailSheetProps) => {
  const { t } = useTranslation()
  const { refetch } = useResourceRecordsContext() as { refetch?: () => void }
  const isOpen = session != null

  const [localReactions, setLocalReactions] = useState(session?.reactions)
  const [userReaction, setUserReaction] = useState(() =>
    session?.my_reactions?.includes?.('like') ? 'like' : null
  )

  useEffect(() => {
    setLocalReactions(session?.reactions)
    setUserReaction(session?.my_reactions?.includes?.('like') ? 'like' : null)
  }, [session])

  useBodyScrollLock(isOpen)

  const kind = session ? getProgrammingKind(session.kind) : null
  const status = session ? getTemporalStatus(session.start_at, session.end_at) : null
  const timeLabel = session
    ? `${DateTime.fromISO(session.start_at).toFormat('HH:mm')} — ${DateTime.fromISO(session.end_at).toFormat('HH:mm')}`
    : ''
  const canCheckIn = session ? canCheckInForSession(session) : false

  const handleReactionChange = (
    newCounts: Record<string, number>,
    newUserReaction: string | null
  ) => {
    setLocalReactions(newCounts)
    setUserReaction(newUserReaction)
    refetch?.()
  }

  return (
    <Drawer.Root open={isOpen} onOpenChange={open => !open && onClose()} dismissible>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-sheet-backdrop bg-black/55" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-sheet mx-auto flex max-h-[90vh] w-full max-w-[400px] flex-col overflow-hidden rounded-t-2xl bg-white focus:outline-none">
          <div className="relative shrink-0 px-4 pt-3 pb-2">
            <Drawer.Handle className="mx-auto h-1 w-10 shrink-0 rounded-full bg-gray-200" />
            <DismissButton
              onClick={onClose}
              aria-label="Fechar"
              className="absolute right-3 top-2"
            />
          </div>

          {session && kind && (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="progress" size="sm" showIcon={false}>
                  {t(kind.nameKey)}
                </Badge>
                {status === 'live' && (
                  <Badge variant="progress" size="sm" showIcon={false}>
                    {t('schedule.status.live')}
                  </Badge>
                )}
                {status === 'past' && (
                  <Badge variant="locked" size="sm" showIcon={false}>
                    {t('schedule.status.done')}
                  </Badge>
                )}
                {session.finished && (
                  <Badge variant="success" size="sm" showIcon>
                    {t('schedule.status.checkedIn')}
                  </Badge>
                )}
              </div>

              <Drawer.Title asChild>
                <h2 className="m-0 text-lg font-bold leading-tight text-gray-900">
                  {session.name}
                </h2>
              </Drawer.Title>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5 text-sm text-gray-700">
                  <Clock01Icon size={14} aria-hidden />
                  {timeLabel}
                </span>
                {session.place?.name && (
                  <span className="flex items-center gap-1.5 text-sm text-gray-700">
                    <Location01Icon size={14} aria-hidden />
                    {session.place.name}
                  </span>
                )}
              </div>

              {session.speakers && session.speakers.length > 0 && (
                <div className="flex flex-col gap-2">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-700">
                    {t('schedule.detail.speakers')}
                  </h3>
                  <div className="flex flex-col gap-2">
                    {session.speakers.map((speaker, index) => (
                      <div key={speaker?.id ?? index} className="flex items-center gap-2.5">
                        <div className="size-8 shrink-0 overflow-hidden rounded-full bg-gray-200">
                          {speaker?.photo?.file?.url ? (
                            <img
                              src={speaker.photo.file.url}
                              alt=""
                              className="size-full object-cover"
                            />
                          ) : (
                            <div className="flex size-full items-center justify-center">
                              <UserCircleIcon size={16} />
                            </div>
                          )}
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {speaker?.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {session.description && (
                <div className="flex flex-col gap-2">
                  <h3 className="m-0 text-xs font-semibold uppercase tracking-wide text-gray-700">
                    {t('schedule.detail.description')}
                  </h3>
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(session.description) }}
                    className="text-sm leading-relaxed text-gray-700"
                  />
                </div>
              )}

              {session.tag_list && session.tag_list.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {session.tag_list.map(tag => (
                    <Badge
                      key={tag}
                      variant="locked"
                      tone="light"
                      size="sm"
                      showIcon={false}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              {session.presentation_link && (
                <a
                  href={session.presentation_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-xl bg-brand/10 px-4 py-3 text-sm font-semibold text-brand no-underline transition-colors hover:bg-brand/15"
                >
                  <Link04Icon size={16} aria-hidden />
                  {t('schedule.detail.presentation')}
                </a>
              )}

              {session.has_rewards && (
                <RewardPill.Group
                  points={session.point_reward}
                  coins={session.coin_reward}
                  direction="row"
                  className="w-auto"
                />
              )}

              <div className="flex items-center gap-3">
                <HeartReactionButton
                  id={session.id}
                  reactableType="Programming"
                  reacted={userReaction}
                  reactions={localReactions}
                  onReactionChange={handleReactionChange}
                  likeAriaLabel="Curtir sessão"
                  unlikeAriaLabel="Remover curtida da sessão"
                  layout="inline"
                />
              </div>

              {/*
                O check-in mora aqui, e não numa folha própria: a palavra é
                anunciada durante a sessão, e quem digita já está lendo esta
                tela. `key` pelo id da sessão descarta o que foi digitado — e o
                erro exibido — ao trocar de sessão sem fechar a folha.
              */}
              {canCheckIn && (
                <ScheduleKeywordCheckInControls
                  key={session.id}
                  session={session}
                  onFinished={onCheckInFinished}
                />
              )}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
